import React, { ComponentProps, useCallback } from "react";
import { defaultLookupId } from "../../utils";
import { useItemsMap, useSelectedIds, useSelectedItems } from "../../../hooks";
import {
  WrappedListComponentType,
  ListWithDataProps,
  BaseListProps,
  ListProps
} from "../types";
import { styles } from "../styles";
import Button from "../../Button";
import ButtonGroup from "../../Button/Group";

/**
 * Simple type guard to determine if a List component props include selectable items.
 */
export function isListWithSelectableItems<T>(
  props: BaseListProps | ListWithDataProps<T>
): props is ListProps<T> & Pick<Required<ListWithDataProps<T>>, "selected"> {
  return "selected" in props && !!props.selected;
}

const ListSelectionHeader = ({
  selectedIds,
  fixed,
  hideCount,
  onClear,
  onSelectAll
}: {
  fixed: Set<string> | undefined;
  selectedIds: string[];
  hideCount: boolean;
  onClear?: (() => void) | false;
  onSelectAll?: (() => void) | false;
}) => {
  const showCount = !hideCount && selectedIds.length > (fixed?.size || 0);

  if (!showCount && !onClear && !onSelectAll) {
    return null;
  }
  return (
    <div css={[styles.listSelectionHeader]}>
      {showCount && (
        <span data-testid="list-info-selected-count">{`${selectedIds.length} selected`}</span>
      )}

      {(onSelectAll || onClear) && (
        <div className="buttons">
          <ButtonGroup>
            {onSelectAll ? (
              <Button variant="ghost" onClick={onSelectAll}>
                Select All
              </Button>
            ) : null}
            {onClear ? (
              <Button variant="ghost" onClick={onClear}>
                Clear
              </Button>
            ) : null}
          </ButtonGroup>
        </div>
      )}
    </div>
  );
};

/**
 * Higher Order Component that adds selecting functionality to a list component.
 * @param WrappedComponent The kit List component to wrap.
 * @returns A new component that can handle selecting, with the initial + additional props
 */
export function listWithSelectableItems<T>(
  WrappedComponent: WrappedListComponentType<T>
) {
  /**
   * List component that includes selecting functionality.
   * @param props The props for the list component. {@link ListWithDataProps}
   */
  const Component = (props: ListWithDataProps<T>) => {
    const { data, header, selected, itemProps } = props;

    const { selectedIds, fixed } = useSelectedIds(
      selected?.value,
      selected?.fixed
    );

    const lookupId = useCallback(
      (item: T) => {
        if (itemProps?.id) {
          return itemProps.id(item) as string;
        }
        return defaultLookupId(item) as string;
      },
      [itemProps?.id]
    );

    const itemsMap = useItemsMap(data, lookupId);
    const selectedItems = useSelectedItems(itemsMap, lookupId, selectedIds);

    const handleItemClick = useCallback(
      (e: React.MouseEvent<HTMLElement>, item: T) => {
        const id = lookupId(item);
        const isDeselecting = selectedIds.includes(id);

        if (
          fixed?.has(id) ||
          (selected?.max &&
            selectedIds.length >= selected?.max &&
            !isDeselecting)
        ) {
          return;
        }
        const nextSelectedIds = isDeselecting
          ? [...selectedIds].filter(selectedId => selectedId !== id)
          : [...selectedIds, id];
        itemProps?.onClick?.(e, item);
        selected?.onChange?.(
          nextSelectedIds,
          nextSelectedIds.map(i => itemsMap.get(i)).filter((t): t is T => !!t)
        );
      },
      [selectedIds, fixed, selected?.max, lookupId]
    );

    const handleOnClear = () => {
      if (selected?.cannotClear) {
        return;
      }
      selected?.onChange?.([], []);
    };

    const handleOnSelectAll = () => {
      selected?.onChange?.([...itemsMap.keys()], [...itemsMap.values()]);
    };
    const showClearButton =
      !selected?.cannotClear && selectedIds.length > (fixed?.size || 0);

    const showSelectAllButton =
      selected?.canSelectAll && selectedIds.length < data.length;

    const hasHeader =
      !!header ||
      showClearButton ||
      showSelectAllButton ||
      (!selected?.hideCount && selectedIds.length > (fixed?.size || 0));

    return (
      <WrappedComponent
        {...(props as ComponentProps<typeof WrappedComponent>)}
        header={
          hasHeader && (
            <>
              {header}
              <ListSelectionHeader
                selectedIds={selectedIds}
                fixed={fixed}
                hideCount={!!selected?.hideCount}
                onClear={showClearButton && handleOnClear}
                onSelectAll={showSelectAllButton && handleOnSelectAll}
              />
            </>
          )
        }
        selectedItems={selectedItems}
        selectedIds={selectedIds}
        itemProps={{
          ...(itemProps || {}),
          id: lookupId,
          onClick:
            itemProps?.onClick || selected?.onChange
              ? handleItemClick
              : undefined
        }}
      />
    );
  };

  return Component;
}
